import { useSearchParams } from 'react-router-dom';
import React, { useState, useMemo } from "react";
import Decimal from "decimal.js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { safeParseDecimal, formatNumberToDisplay } from "@/lib/api/common";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { cn } from "@/lib/utils";
import { get_groupable_commodity_display_name, Commodities } from "@/lib/api/commodity";
import { get_customer_commodity_top_level } from "../common";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { useQueryParam } from "@/hooks/use-query-param";

export interface RevenueField {
  quantity: Decimal;
  value: Decimal;
  per_acre: Decimal;
}

export interface RiskRevenueSummaryProps {
  baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>;
  className?: string;
}

export interface CostOfProduction {
    cost_per_acre: Decimal;
    acreage: Decimal;

    projected_yield: Decimal;
    actual_yield: Decimal;
    total_yield: Decimal;
    total_production: Decimal;
    total_cost: Decimal;
}

export interface RevenueBreakdownTypes {
  sold: {
      revenue: RevenueField
      total: { revenue: RevenueField };
  };
  unknown: {
      total: { revenue: RevenueField };
  };
  unsold: {
    futures: { revenue: RevenueField };
    basis: { revenue: RevenueField };
    unset: { revenue: RevenueField };
    total: { revenue: RevenueField };
  };
  brokerage: {
    unrealized: RevenueField;
    realized: RevenueField;
    total: { revenue: RevenueField };
  };
  // Breakout type or NonBreakout
  cost_of_production: {
      Base: CostOfProduction
  };
}

export function RiskRevenueSummary({ baseTopLevelResult, className }: RiskRevenueSummaryProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedType, setSelectedType] = useState<string>("Overview");
    const [customerIds, _customerIds] = useQueryParamArray("customer_ids");

    const selectedCustomerId = customerIds?.[0];

    const {
        comboboxOptions,
        commodityRevenueData,
        overviewRevenueData,
    } = useMemo(() => {
        const summaryData = baseTopLevelResult?.data?.data?.summary;
        
        if (!summaryData) {
            return { comboboxOptions: [], commodityRevenueData: {}, overviewRevenueData: [] };
        }
        
        // Determine which customers to use
        const availableCustomerIds = Object.keys(summaryData.customer_name_map || {});
        const targetCustomerIds = customerIds.length > 0 ? customerIds : availableCustomerIds;
        
        if (targetCustomerIds.length === 0) {
            return { comboboxOptions: [], commodityRevenueData: {}, overviewRevenueData: [] };
        }
        
        const selectableCommodityTypes = summaryData.commodity_list || [];
        const commodityRevenueData: Record<string, any[]> = {};
        
        // Get revenue_data for each commodity, aggregating across all target customers
        selectableCommodityTypes.forEach((commodityType: Commodities) => {
            const aggregatedCommodityValues = {
                sold_revenue: new Decimal(0),
                sold_quantity: new Decimal(0),
                unsold_projected: new Decimal(0),
                unsold_quantity: new Decimal(0),
                hedging: new Decimal(0),
                hedging_quantity: new Decimal(0),
                cost_of_production: new Decimal(0),
                cost_of_production_quantity: new Decimal(0),
                unknown: new Decimal(0),
                total_acreage: new Decimal(0),

                hta_otc_projected: new Decimal(0),
                hta_otc_projected_quantity: new Decimal(0),

                basis_unprotected_projected: new Decimal(0),
                basis_unprotected_projected_quantity: new Decimal(0),
            };

            targetCustomerIds.forEach(customerId => {
                const commodityData = get_customer_commodity_top_level({
                    commodity_type: commodityType,
                    customer_id: customerId,
                    summaryData: summaryData
                });
                
                // Get revenue breakdown data directly to access quantities
                const base_revenue_data = summaryData.revenue_breakdown?.[commodityType]?.[customerId];
                
                if (commodityData?.revenue_data) {
                    commodityData.revenue_data.forEach((item: any) => {
                        switch (item.id) {
                            case 'sold_revenue':
                                aggregatedCommodityValues.sold_revenue = aggregatedCommodityValues.sold_revenue.plus(safeParseDecimal({})(item.value));
                                aggregatedCommodityValues.sold_quantity = aggregatedCommodityValues.sold_quantity.plus(safeParseDecimal({})(item.quantity));
                                break;
                            case 'unsold_projected':
                                aggregatedCommodityValues.unsold_projected = aggregatedCommodityValues.unsold_projected.plus(safeParseDecimal({})(item.value));
                                aggregatedCommodityValues.unsold_quantity = aggregatedCommodityValues.unsold_quantity.plus(safeParseDecimal({})(item.quantity));
                                break;
                            case 'hta_otc_projected':
                                aggregatedCommodityValues.hta_otc_projected = aggregatedCommodityValues.hta_otc_projected.plus(safeParseDecimal({})(item.value));
                                aggregatedCommodityValues.hta_otc_projected_quantity = aggregatedCommodityValues.hta_otc_projected_quantity.plus(safeParseDecimal({})(item.quantity));
                                break;
                            case 'basis_unprotected_projected':
                                aggregatedCommodityValues.basis_unprotected_projected = aggregatedCommodityValues.basis_unprotected_projected.plus(safeParseDecimal({})(item.value));
                                aggregatedCommodityValues.basis_unprotected_projected_quantity = aggregatedCommodityValues.basis_unprotected_projected_quantity.plus(safeParseDecimal({})(item.quantity));
                                break;
                            case 'heding_gain_loss':
                                aggregatedCommodityValues.hedging = aggregatedCommodityValues.hedging.plus(safeParseDecimal({})(item.value));
                                aggregatedCommodityValues.hedging_quantity = aggregatedCommodityValues.hedging_quantity.plus(safeParseDecimal({})(item.quantity));
                                break;
                            case 'cost_of_production':
                                aggregatedCommodityValues.cost_of_production_quantity = aggregatedCommodityValues.cost_of_production_quantity.plus(safeParseDecimal({})(item.quantity));
                                aggregatedCommodityValues.cost_of_production = aggregatedCommodityValues.cost_of_production.plus(safeParseDecimal({})(item.value));
                                if (item.per_acre && item.value) {
                                    const itemValue = safeParseDecimal({})(item.value);
                                    const itemPerAcre = safeParseDecimal({})(item.per_acre);
                                    const acreage = itemPerAcre.gt(0) ? itemValue.div(itemPerAcre) : new Decimal(0);
                                    aggregatedCommodityValues.total_acreage = aggregatedCommodityValues.total_acreage.plus(acreage);
                                }
                                break;
                            case 'unknown':
                                aggregatedCommodityValues.unknown = aggregatedCommodityValues.unknown.plus(safeParseDecimal({})(item.value));
                                break;
                        }
                    });
                }
            });
            
            // Create aggregated revenue_data for this commodity
            const commodityRevenue = [
                { 
                    label: "Sold Revenue", 
                    id: 'sold_revenue', 
                    value: aggregatedCommodityValues.sold_revenue.toNumber(),
                    quantity: aggregatedCommodityValues.sold_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.sold_revenue.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.sold_quantity.gt(0) ? aggregatedCommodityValues.sold_revenue.div(aggregatedCommodityValues.sold_quantity).toNumber() : 0
                },
                { 
                    label: "Unsold Projected", 
                    id: 'unsold_projected', 
                    value: aggregatedCommodityValues.unsold_projected.toNumber(),
                    quantity: aggregatedCommodityValues.unsold_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.unsold_projected.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.unsold_quantity.gt(0) ? aggregatedCommodityValues.unsold_projected.div(aggregatedCommodityValues.unsold_quantity).toNumber() : 0
                },
                {
                    label: "Basis/OTC Projected",
                    id: 'basis_unprotected_projected',
                    value: aggregatedCommodityValues.basis_unprotected_projected.toNumber(),
                    quantity: aggregatedCommodityValues.basis_unprotected_projected_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.basis_unprotected_projected.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.basis_unprotected_projected_quantity.gt(0) ? aggregatedCommodityValues.basis_unprotected_projected.div(aggregatedCommodityValues.basis_unprotected_projected_quantity).toNumber() : 0
                },
                {
                    label: "HTA/OTC Projected",
                    id: 'hta_otc_projected',
                    value: aggregatedCommodityValues.hta_otc_projected.toNumber(),
                    quantity: aggregatedCommodityValues.hta_otc_projected_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.hta_otc_projected.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.hta_otc_projected_quantity.gt(0) ? aggregatedCommodityValues.hta_otc_projected.div(aggregatedCommodityValues.hta_otc_projected_quantity).toNumber() : 0
                },
                {
                    label: "Hedging", 
                    id: 'heding_gain_loss', 
                    value: aggregatedCommodityValues.hedging.toNumber(),
                    quantity: aggregatedCommodityValues.hedging_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.hedging.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.hedging_quantity.gt(0) ? aggregatedCommodityValues.hedging.div(aggregatedCommodityValues.hedging_quantity).toNumber() : 0
                },
                { 
                    label: "cost_of_production", 
                    id: 'cost_of_production', 
                    value: aggregatedCommodityValues.cost_of_production.toNumber(),
                    quantity: aggregatedCommodityValues.cost_of_production_quantity.toNumber(),
                    per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? aggregatedCommodityValues.cost_of_production.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                    per_uom: aggregatedCommodityValues.cost_of_production_quantity.gt(0) ? aggregatedCommodityValues.cost_of_production.div(aggregatedCommodityValues.cost_of_production_quantity).toNumber() : 0
                }
            ];


            // Add profit calculation for this commodity
            const profit = aggregatedCommodityValues.sold_revenue
                .plus(aggregatedCommodityValues.unsold_projected)
                .plus(aggregatedCommodityValues.hedging)
                .minus(aggregatedCommodityValues.cost_of_production);

            const totalQuantity = aggregatedCommodityValues.sold_quantity
                .plus(aggregatedCommodityValues.unsold_quantity)
                .plus(aggregatedCommodityValues.hedging_quantity);

            commodityRevenue.push({
                label: "Profit",
                id: 'profit',
                value: profit.toNumber(),
                quantity: 0,
                per_acre: aggregatedCommodityValues.total_acreage.gt(0) ? profit.div(aggregatedCommodityValues.total_acreage).toNumber() : 0,
                per_uom: totalQuantity.gt(0) ? profit.div(totalQuantity).toNumber() : 0
            });
            
            // Add unknown if it exists
            if (aggregatedCommodityValues.unknown.gt(0)) {
                commodityRevenue.push({ 
                    label: "Unknown", 
                    id: 'unknown',
                    quantity: 0,
                    value: aggregatedCommodityValues.unknown.toNumber(), 
                    per_acre: 0,
                    per_uom: 0
                });
            }
            
            commodityRevenueData[commodityType] = commodityRevenue;
        });
        
        // Aggregate all commodity revenue_data for Overview
        const overviewRevenueData = [];
        const aggregatedValues = {
            sold_revenue: new Decimal(0),
            sold_quantity: new Decimal(0),
            unsold_projected: new Decimal(0),
            unsold_quantity: new Decimal(0),
            hedging: new Decimal(0),
            hedging_quantity: new Decimal(0),
            cost_of_production: new Decimal(0),
            cost_of_production_quantity: new Decimal(0),
            unknown: new Decimal(0),

            hta_otc_projected: new Decimal(0),
            hta_otc_projected_quantity: new Decimal(0),

            basis_unprotected_projected: new Decimal(0),
            basis_unprotected_projected_quantity: new Decimal(0),
        };

        Object.values(commodityRevenueData).forEach((revenueData: any[]) => {
            revenueData.forEach((item: any) => {
                switch (item.id) {
                    case 'sold_revenue':
                        aggregatedValues.sold_quantity = aggregatedValues.sold_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.sold_revenue = aggregatedValues.sold_revenue.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'unsold_projected':
                        aggregatedValues.unsold_quantity = aggregatedValues.unsold_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.unsold_projected = aggregatedValues.unsold_projected.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'hta_otc_projected':
                        aggregatedValues.hta_otc_projected_quantity = aggregatedValues.hta_otc_projected_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.hta_otc_projected = aggregatedValues.hta_otc_projected.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'basis_unprotected_projected':
                        aggregatedValues.basis_unprotected_projected_quantity = aggregatedValues.basis_unprotected_projected_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.basis_unprotected_projected = aggregatedValues.basis_unprotected_projected.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'heding_gain_loss':
                        aggregatedValues.hedging_quantity = aggregatedValues.hedging_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.hedging = aggregatedValues.hedging.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'cost_of_production':
                        aggregatedValues.cost_of_production_quantity = aggregatedValues.cost_of_production_quantity.plus(safeParseDecimal({})(item.quantity));
                        aggregatedValues.cost_of_production = aggregatedValues.cost_of_production.plus(safeParseDecimal({})(item.value));
                        break;
                    case 'unknown':
                        aggregatedValues.unknown = aggregatedValues.unknown.plus(safeParseDecimal({})(item.value));
                        break;
                }
            });
        });
        
        // Calculate total acreage for per_acre calculations
        const totalAcreage = Object.values(commodityRevenueData).reduce((total, revenueData: any[]) => {
            const costItem = revenueData.find((item: any) => item.id === 'cost_of_production');
            if (costItem?.per_acre && costItem?.value) {
                const itemValue = safeParseDecimal({})(costItem.value);
                const itemPerAcre = safeParseDecimal({})(costItem.per_acre);
                const acreage = itemPerAcre.gt(0) ? itemValue.div(itemPerAcre) : new Decimal(0);
                return total.plus(acreage);
            }
            return total;
        }, new Decimal(0));
        
        // Create overview revenue_data array
        overviewRevenueData.push(
            { 
                label: "Sold Revenue", 
                id: 'sold_revenue', 
                value: aggregatedValues.sold_revenue.toNumber(), 
                per_acre: totalAcreage.gt(0) ? aggregatedValues.sold_revenue.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.sold_quantity.gt(0) ? aggregatedValues.sold_revenue.div(aggregatedValues.sold_quantity).toNumber() : 0
            },
            { 
                label: "Unsold Projected", 
                id: 'unsold_projected', 
                value: aggregatedValues.unsold_projected.toNumber(), 
                per_acre: totalAcreage.gt(0) ? aggregatedValues.unsold_projected.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.unsold_quantity.gt(0) ? aggregatedValues.unsold_projected.div(aggregatedValues.unsold_quantity).toNumber() : 0
            },
            {
                label: "HTA/OTC Projected",
                id: 'hta_otc_projected',
                value: aggregatedValues.hta_otc_projected.toNumber(),
                quantity: aggregatedValues.hta_otc_projected_quantity,
                per_acre: totalAcreage.gt(0) ? aggregatedValues.hta_otc_projected.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.hta_otc_projected_quantity.gt(0) ? aggregatedValues.hta_otc_projected.div(aggregatedValues.hta_otc_projected_quantity).toNumber() : 0
            },
            { 
                label: "Basis/Unprotected Proj",
                id: 'basis_unprotected_projected',
                value: aggregatedValues.basis_unprotected_projected.toNumber(),
                quantity: aggregatedValues.basis_unprotected_projected_quantity,
                per_acre: totalAcreage.gt(0) ? aggregatedValues.basis_unprotected_projected.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.basis_unprotected_projected_quantity.gt(0) ? aggregatedValues.basis_unprotected_projected.div(aggregatedValues.basis_unprotected_projected_quantity).toNumber() : 0
            },
            {
                label: "Hedging",
                id: 'hedging',
                value: aggregatedValues.hedging.toNumber(),
                quantity: aggregatedValues.hedging_quantity,
                per_acre: totalAcreage.gt(0) ? aggregatedValues.hedging.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.hedging_quantity.gt(0) ? aggregatedValues.hedging.div(aggregatedValues.hedging_quantity).toNumber() : 0
            },
            { 
                label: "cost_of_production", 
                id: 'cost_of_production', 
                value: aggregatedValues.cost_of_production.toNumber(), 
                per_acre: totalAcreage.gt(0) ? aggregatedValues.cost_of_production.div(totalAcreage).toNumber() : 0,
                per_uom: aggregatedValues.cost_of_production_quantity.gt(0) ? aggregatedValues.cost_of_production.div(aggregatedValues.cost_of_production_quantity).toNumber() : 0
            }
        );

        // Add profit calculation
        const profit = aggregatedValues.sold_revenue
            .plus(aggregatedValues.unsold_projected)
            .plus(aggregatedValues.hedging)
            .minus(aggregatedValues.cost_of_production);

        const totalQuantity = aggregatedValues.sold_quantity
            .plus(aggregatedValues.unsold_quantity)
            .plus(aggregatedValues.hedging_quantity);

        overviewRevenueData.push({
            label: "Profit",
            id: 'profit',
            value: profit.toNumber(),
            per_acre: totalAcreage.gt(0) ? profit.div(totalAcreage).toNumber() : 0,
            per_uom: totalQuantity.gt(0) ? profit.div(totalQuantity).toNumber() : 0
        });
        
        // Add unknown if it exists
        if (aggregatedValues.unknown.gt(0)) {
            overviewRevenueData.push({ 
                label: "Unknown", 
                id: 'unknown', 
                value: aggregatedValues.unknown.toNumber(), 
                per_acre: 0,
                per_uom: 0
            });
        }
        
        const comboboxOptions = [
            { value: 'Overview', label: 'Overview' },
            ...selectableCommodityTypes.map((commodityType: Commodities) => ({
                value: commodityType,
                label: get_groupable_commodity_display_name(commodityType)
            }))
        ];

        return {
            comboboxOptions,
            commodityRevenueData,
            overviewRevenueData,
        };
    }, [customerIds, baseTopLevelResult])


    // Get current revenue data based on selected type
    const currentRevenueData = selectedType === 'Overview' ? overviewRevenueData : commodityRevenueData[selectedType] || [];
    const isCanola = /cnl|canola/im.test(selectedType || "");

    const hasData = baseTopLevelResult?.data?.data?.summary;
    const hasValues = comboboxOptions.length > 1; // More than just Overview
    const formatCurrency = (value: string | Decimal | number | undefined) => formatNumberToDisplay(value, { isCurrency: true, emptyFormat: "--", ...(isCanola ? ({
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }) : ({})) });
    const formatCurrencyPerAcre = (value: string | Decimal | number | undefined) => formatNumberToDisplay(value, { isCurrency: true, emptyFormat: "--", suffix: "/acre" });
    const formatCurrencyPerUom = (value: string | Decimal | number | undefined, commodity?: string) => formatNumberToDisplay(value, { isCurrency: true, emptyFormat: "--", suffix: isCanola ? "$/lb" : "$/bu", ...(isCanola ? ({
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }) : ({})) });

    // Extract values from revenue data
    const soldRevenue = currentRevenueData.find(item => item.id === 'sold_revenue');
    const unsoldProjected = currentRevenueData.find(item => item.id === 'unsold_projected');
    const unsoldHtaOtcProjected = currentRevenueData.find(item => item.id === 'hta_otc_projected');
    const unsoldBasisUnproProjected = currentRevenueData.find(item => item.id === 'basis_unprotected_projected');
    const hedgingGainLoss = currentRevenueData.find(item => item.id === 'heding_gain_loss' || item.id === 'hedging');
    const costOfProduction = currentRevenueData.find(item => item.id === 'cost_of_production');
    const profit = currentRevenueData.find(item => item.id === 'profit');
    const unknown = currentRevenueData.find(item => item.id === 'unknown');

    const profitValue = new Decimal(profit?.value || 0);
    const isProfitPositive = profitValue.gte(0);
    const has_unknown = unknown && unknown.value > 0;


    const handleRevenueClick = (revenueType: "sold" | "unsold" | "hedgeable_positions") => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("overview_tab", "revenue");
        newSearchParams.set("revenue_breakdown_detail", revenueType);
        setSearchParams(newSearchParams);
    };

    return (
        <Card className={cn("w-full h-full", className)}>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    Revenue Summary
                    <Combobox
                        disabled={!hasData}
                        value={selectedType}
                        onValueChange={(item) => setSelectedType(item || "")}
                        options={comboboxOptions}
                        placeholder="Select type"
                        className="w-48"
                        dwimOnChange
                    />
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {(!hasData || !hasValues) ? (
                    <div className="text-center py-8 text-gray-500">
                        {!hasData ? "Loading data..." : !selectedType ? "Select a commodity" : "No Revenue Data"}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div 
                                    className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                    onClick={() => handleRevenueClick("sold")}
                                >
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">Sold Revenue</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(soldRevenue?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(soldRevenue?.per_uom, selectedType)}</div>
                                    </div>
                                </div>
                                
                                <div 
                                    className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                    onClick={() => handleRevenueClick("unsold")}
                                >
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">Unsold Projected</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(unsoldProjected?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(unsoldProjected?.per_uom, selectedType)}</div>
                                    </div>
                                </div>

                                <div
                                    className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                    onClick={() => handleRevenueClick("unsold")}
                                >
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">HTA/OTC Projected</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(unsoldHtaOtcProjected?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(unsoldHtaOtcProjected?.per_uom, selectedType)}</div>
                                    </div>
                                </div>

                                <div
                                    className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                    onClick={() => handleRevenueClick("unsold")}
                                >
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">Basis/Unprotected Projected</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(unsoldBasisUnproProjected?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(unsoldBasisUnproProjected?.per_uom, selectedType)}</div>
                                    </div>
                                </div>

                                <div 
                                    className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                    onClick={() => handleRevenueClick("hedgeable_positions")}
                                >
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">Hedging Gain/Loss</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(hedgingGainLoss?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(hedgingGainLoss?.per_uom, selectedType)}</div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-sm font-medium text-gray-600 mb-2">Cost of Production</h3>
                                    <div className="space-y-1">
                                        <div className="text-lg font-semibold">{formatCurrency(costOfProduction?.value)}</div>
                                        <div className="text-sm text-gray-500">{selectedType === "Overview" ? "" : formatCurrencyPerUom(costOfProduction?.per_uom, selectedType)}</div>
                                    </div>
                                </div>
                        </div>

                        <div className={cn(
                            "border-t pt-4 grid grid-cols-1 gap-4",

                        )}>
                            <div className={cn(
                                "p-4 rounded-lg border",
                                isProfitPositive 
                                    ? "bg-green-50 border-green-200 text-green-800"
                                    : "bg-red-50 border-red-200 text-red-800"
                            )}>
                                <h3 className="text-sm font-medium mb-2">Profit</h3>
                                <div className="space-y-1">
                                    <div className="text-xl font-bold">{formatCurrency(profit?.value)}</div>
                                    <div className="text-sm opacity-80">{selectedType === "Overview" ? "" : formatCurrencyPerUom(profit?.per_uom, selectedType)}</div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
